// P4 AI Tutor -- Vuex module (08 §9, architecture; WP-03 fills in the
// send/SSE lifecycle 08 §9 describes).
// WP-01 shipped state shape + mutations only. WP-02 added ONLY the setup
// half of `startSession`: commit `activeSetupContext`, call the real
// `api.createSession`, commit `activeSession`. WP-03 (doc 09 §WP-03) adds the
// rest: `openTutorStream`/`sendMessage`/`refetchActiveSession`/
// `stopStreaming`. STUDENT CHAT V2 -- CORE INTERACTION LOOP (human-authorized
// 2026-09-22) adds the optimistic student message, the waiting state's store
// signal and `submitFeedback` («فهمیدم / نفهمیدم» through Subway).
//
// WP-03-CHAT-CORRECTION-R3 (doc 11, human-authorized 2026-09-19) adds, on the
// borna-console `ChatPanel.tsx` behavior: `loadSessions` (the basic session
// list) and `openSession(id)` (re-open an existing session), a live reply
// that is reconciled with the authoritative refetch instead of vanishing on
// `done`, and stale-response guards so a late refetch can never overwrite a
// different session that has since become active.
//
// `collaborators` is the manual-dependency-injection seam this file's own
// unit tests use in place of `jest.mock` (09 §3's binding Jest-24/26
// constraint: any spec using `jest.mock` fails to even run in this repo).
// Tests reassign `collaborators.api`/`collaborators.adapter`/
// `collaborators.sseClient` to plain fake objects before dispatching an
// action -- no module-mocking machinery.
import * as api from '@/services/aiTutor/api'
import * as adapter from '@/services/aiTutor/adapter'
// AT-API-020-H1: pure predicates and a stable code -- not DTO mapping, so
// not part of the `collaborators` seam below.
import {
  ACTION_REJECTED_CODE,
  isActionId,
  resolveActionId,
} from '@/services/aiTutor/adapter'
import * as sseClient from '@/services/aiTutor/sseClient'
// AT-API-020-H4 (doc 34/01 §3.5): the Board boundary on a share's explicit
// request -- a pure predicate, like the two above.
import { isBoardShareActionId } from '@/services/aiTutor/boardShare'
import { revokePreviewUrl } from '@/services/aiTutor/imageFile'
import { mintRequestId } from '@/services/aiTutor/requestId'

export const collaborators = { api, adapter, sseClient, mintRequestId }

// CAT-1A (doc 20 §8.3) -- the client request id of the setup submission
// being started, held outside Vuex state like `activeStream`. Minted when a
// NEW submission starts; kept across a failed attempt so the student's retry
// of the same setup reuses it (Subway then returns the conversation it
// already started instead of creating another); cleared once a session is
// active.
let startRequestId = null

// WP-03 -- the live SSE subscription handle ({stop}) for the current
// activeSession, held OUTSIDE Vuex state on purpose (a non-serializable
// object with a live AbortController is not state Vuex should track/observe
// reactively). Exactly one subscription is ever open at a time; opening a
// new one always stops the previous one first (08 §9's stop-on-session-
// change rule).
let activeStream = null

// WP-03-CHAT-CORRECTION-R3 -- monotonically increasing request tokens, held
// outside Vuex state like `activeStream`. `refetchSeq` lets only the newest
// refetch update the session-level snapshot (turn upserts are idempotent, so
// an older response may still merge its turns); `openSeq` lets only the most
// recent session selection win when several are in flight.
let refetchSeq = 0
let openSeq = 0

// STUDENT CHAT V2 -- live replies already settled from an HTTP response
// (`createTurn` returns the persisted tutor turn, and the upstream publishes
// the same reply on SSE; AT-API-020-H2: `createFeedback` returns no turn any
// more). Keyed by the SSE `turnId`, i.e. the turn the reply answers
// (`replyToTurnId`). A late SSE event for a key in here would otherwise
// re-draw a reply that is already on screen as a turn.
let settledLiveKeys = new Set()

/**
 * AT-API-020-H2 (doc 34/01 §3.6, doc 34/07 §6.2) -- the tutor turn a message
 * is the student's CLARIFICATION of: the conversation's last turn is the
 * tutor's, the student marked it «نفهمیدم» (recorded by Subway) and no reply
 * has answered that yet. The reply to such a message is drawn as the
 * "re-teach" of that turn (retry lead + retry prompt copy), exactly as the
 * provider-run re-teach used to be. Null otherwise.
 * @param {import('@/services/aiTutor/domain').TutorTurn[]|undefined} turns
 * @param {Object<string, {kind: string, status: string, reteachTurnId: string|null}>} feedbackByTurnId
 * @returns {string|null}
 */
export function clarifiedTurnIdFor(turns, feedbackByTurnId) {
  const list = Array.isArray(turns) ? turns : []
  const last = list[list.length - 1]
  if (!last || last.role === 'student') return null
  const entry = (feedbackByTurnId || {})[last.id]
  return entry &&
    entry.kind === 'confused' &&
    entry.status === 'recorded' &&
    !entry.reteachTurnId
    ? last.id
    : null
}

// STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW: the File behind the optimistic
// message's image ({file, fileFormat, remoteFilename}), held outside Vuex
// state like `activeStream` (a File is not state to observe). Kept until the
// message is reconciled so a resend can reuse an upload that already
// succeeded; `remoteFilename` stays null until the S3 upload has completed.
//
// WB-M2A (doc 31): for a board picture it also keeps the Borna-only share
// descriptor (`boardShare`, with its stable share id) and the explicit
// request (`request`: the chosen kind of help), so a resend sends the SAME
// frozen picture under the SAME share id -- never a new capture, never a
// second share.
//
// AT-API-020-H1: `request` is `{actionId}` (API 0.2.0), or null. It is
// cleared when the Tutor refuses that action, so a resend of the message
// travels without it.
let pendingUpload = null

// AT-API-020-H1 (doc 34/01 §3.2) -- the course catalog read in flight
// ({courseName, token, promise}), held outside Vuex state like
// `activeStream`. `catalogSeq` lets only the NEWEST read write the store, so
// an answer that arrives late (the Student has moved to another course, or a
// forced re-read has superseded it) changes nothing.
let catalogSeq = 0
let catalogInFlight = null

// Reads the Tutor catalog of ONE course through Subway and resolves to it
// ({courseName, actions, teacherHandoff}), or to null when it could not be
// read. Never rejects and never touches `error`: the catalog only decides
// whether a turn may carry an action, and without it a turn simply travels
// as text. A read already in flight for the same course is shared unless
// `force` asks for a fresh one (after the Tutor refused an action, the
// answer in flight may be the stale one).
function readCatalog({ commit }, courseName, force) {
  if (!courseName) return Promise.resolve(null)
  if (!force && catalogInFlight && catalogInFlight.courseName === courseName) {
    return catalogInFlight.promise
  }
  catalogSeq += 1
  const token = catalogSeq
  commit('setCatalogStatus', 'loading')
  // Registered BEFORE the read starts: a transport that fails synchronously
  // settles the read before this function returns, and its `finally` must
  // find (and clear) this very entry -- an entry registered afterwards would
  // stay behind as a finished "read in flight" and answer every later read
  // of the course with nothing.
  const entry = { courseName, token, promise: null }
  catalogInFlight = entry
  entry.promise = (async () => {
    try {
      const dto = await collaborators.api.getCatalog(courseName)
      const catalog = {
        courseName,
        ...collaborators.adapter.toTutorCatalog(dto),
      }
      if (token === catalogSeq) {
        commit('setCatalog', catalog)
        commit('setCatalogStatus', 'loaded')
      }
      return catalog
    } catch (err) {
      // No catalog is safer than an old one: nothing can be sent as an
      // action until a read succeeds.
      if (token === catalogSeq) {
        commit('setCatalog', null)
        commit('setCatalogStatus', 'error')
      }
      return null
    } finally {
      if (catalogInFlight === entry) catalogInFlight = null
    }
  })()
  return entry.promise
}

// The catalog a send decides against: the one already read for this course,
// else a read (the one in flight, or a new one). Null when it cannot be read.
function catalogForSend(context, courseName) {
  const { state } = context
  if (
    state.catalogStatus === 'loaded' &&
    state.catalog &&
    state.catalog.courseName === courseName
  ) {
    return Promise.resolve(state.catalog)
  }
  return readCatalog(context, courseName, false)
}

// CURRENT CONTRACT AUDIT -- a persisted photo's `attachmentUrls` entry is a
// signed GET that upstream re-signs on every getTurns (15 min lifetime,
// tutor-attachment.constant.ts). A photo that fails to load triggers one
// refetch; failures arriving together share it, and a turn whose photo still
// fails after two refreshes is left alone (the object itself is gone).
const MEDIA_REFRESH_MAX_PER_TURN = 2
const MEDIA_REFRESH_COALESCE_MS = 5000
let mediaRefresh = { attempts: {}, lastAt: 0 }

// BUG-22 (AI Tutor SSE reliability) -- the event stream's TRANSPORT is
// tracked apart from the Tutor's reply (`processingState`): the stream is
// open for as long as a session is, idle or not, so losing it says nothing
// about a reply. An unexpected loss is reconnected with a bounded backoff; a
// connection that delivered a Tutor event or stayed up for `stableMs`
// earns a fresh budget. Only a loss while a reply is being generated triggers
// recovery: bounded re-reads of the persisted session (never a resend), and
// a reply failure only once those are exhausted.
export const streamReliability = {
  reconnectDelaysMs: [1000, 3000, 7000, 15000, 30000],
  stableMs: 30000,
  recoveryDelaysMs: [2000, 4000, 8000, 15000, 30000, 30000],
}
// Only these Subway codes (sent in its in-stream failure notice) are worth
// reconnecting for; any other code (session not found, credential rejected,
// ...) would fail the same way again.
const RETRYABLE_STREAM_CODES = ['AI_TUTOR_UNAVAILABLE']

// Bumped whenever the stream is (re)opened for a session or stopped: a
// handler, reconnect timer or recovery from an older generation is inert, so
// a stale reconnect can never attach to a newer session.
let streamSeq = 0
let reconnectAttempt = 0
let reconnectTimer = null
let recoverySeq = 0

function countStudentTurns(turns) {
  return (turns || []).filter(turn => turn.role === 'student').length
}

function countTutorTurns(turns) {
  return (turns || []).filter(turn => turn.role !== 'student').length
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// WB-M2A (doc 31 §7) -- the conversation's Borna share records (the «از تخته»
// provenance), read once from Subway's conversation read. Best-effort: a
// failure only leaves the badges off; it never touches `error`.
async function readBoardShares(commit, conversationId) {
  if (!conversationId) return
  try {
    const dto = await collaborators.api.getConversation(conversationId)
    commit(
      'setBoardShares',
      collaborators.adapter.toBoardShareByTurnId(dto && dto.boardShares)
    )
  } catch (err) {
    // The badge is secondary; the conversation itself is unaffected.
  }
}

// CR-03 (doc 28 §16) -- the Student AI Tutor credit balance lives in the user
// store and only ever comes from Subway. These are best-effort side channels:
// whatever the user store does (or a test double's `dispatch` lacks), they
// can neither throw into nor delay the tutor flow.
function toUserStore(dispatch, action, payload) {
  try {
    Promise.resolve(dispatch(`user/${action}`, payload, { root: true })).catch(
      () => undefined
    )
  } catch (err) {
    // The credit display is secondary; the send itself is unaffected.
  }
}

// After a successful createTurn: Subway's own balance wins, as reported. An
// explicit null (Subway could not state it safely) asks Subway again rather
// than inventing a value; an absent field changes nothing.
function syncCreditsFromReply(dispatch, reply) {
  if (!reply || typeof reply !== 'object') return
  const credits = reply.aiTutorCreditsRemaining
  if (typeof credits === 'number' && Number.isFinite(credits)) {
    toUserStore(dispatch, 'addToUser', { aiTutorCreditsRemaining: credits })
  } else if (credits === null) {
    toUserStore(dispatch, 'fetchUser')
  }
}

// After a failed createTurn: exhausted -> Subway said the balance is 0; turn
// in progress -> the balance is unchanged; anything else may have been
// charged or not, so the balance is re-read once in the background (the
// original error stays what the Student sees).
function syncCreditsAfterFailure(dispatch, error) {
  const kind = error && error.kind
  if (kind === 'credits_exhausted') {
    toUserStore(dispatch, 'addToUser', { aiTutorCreditsRemaining: 0 })
  } else if (kind !== 'turn_in_progress') {
    toUserStore(dispatch, 'fetchUser')
  }
}

// Stops the open stream and any pending reconnect or reply recovery.
function closeTutorStream() {
  streamSeq += 1
  recoverySeq += 1
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (activeStream) {
    activeStream.stop()
    activeStream = null
  }
}

/** @param {import('@/services/aiTutor/sseClient').StreamLoss} loss */
function isRetryableLoss(loss) {
  if (loss.reason === 'http') {
    return (
      !loss.status ||
      loss.status >= 500 ||
      loss.status === 408 ||
      loss.status === 429
    )
  }
  if (loss.reason === 'unavailable') {
    return !loss.code || RETRYABLE_STREAM_CODES.includes(loss.code)
  }
  return true
}

// The interrupted reply has landed: a tutor turn after the student turn the
// live reply answered (the SSE `turnId`), or any tutor turn newer than the
// ones the session had when the stream dropped.
function replyPersisted(turns, turnId, tutorCountAtLoss) {
  const list = turns || []
  if (turnId) {
    const index = list.findIndex(turn => turn.id === turnId)
    if (
      index !== -1 &&
      list.slice(index + 1).some(turn => turn.role !== 'student')
    ) {
      return true
    }
  }
  return countTutorTurns(list) > tutorCountAtLoss
}

function scheduleReconnect(context, sessionId, courseName) {
  const { commit, state } = context
  const delay = streamReliability.reconnectDelaysMs[reconnectAttempt]
  reconnectAttempt += 1
  commit('setStreamStatus', 'reconnecting')
  const generation = streamSeq
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (generation !== streamSeq) return
    if (state.activeSession?.id !== sessionId || activeStream) return
    connectTutorStream(context, sessionId, courseName)
  }, delay)
}

function handleStreamLoss(context, sessionId, courseName, loss, stable) {
  const { commit, state, dispatch } = context
  if (stable) reconnectAttempt = 0
  const phase = state.processingState.phase
  if (phase === 'processing' || phase === 'streaming') {
    dispatch('recoverInterruptedReply', sessionId)
  }
  if (
    !isRetryableLoss(loss) ||
    reconnectAttempt >= streamReliability.reconnectDelaysMs.length
  ) {
    commit('setStreamStatus', 'unavailable')
    return
  }
  scheduleReconnect(context, sessionId, courseName)
}

// Opens one connection of the current stream generation. Never touches
// `processingState`, so a reconnect cannot wipe a reply in progress.
function connectTutorStream(context, sessionId, courseName) {
  const { commit, state, dispatch } = context
  const generation = streamSeq
  const connectedAt = Date.now()
  let deliveredEvent = false
  const current = () =>
    generation === streamSeq && state.activeSession?.id === sessionId
  const settled = event => settledLiveKeys.has(event && event.turnId)
  const received = () => {
    deliveredEvent = true
    reconnectAttempt = 0
  }
  // BUG-046: the stream has the message in flight -- it is sent.
  const delivered = event => {
    if (eventOfPendingSend(state, sessionId, event)) {
      markPendingSent(context, sessionId, 'stream')
    }
  }

  commit(
    'setStreamStatus',
    reconnectAttempt > 0 ? 'reconnecting' : 'connecting'
  )
  activeStream = collaborators.sseClient.openTutorEventStream(
    courseName,
    sessionId,
    {
      onOpen: () => {
        if (current()) commit('setStreamStatus', 'open')
      },
      onState: event => {
        if (!current()) return
        received()
        if (settled(event)) return
        delivered(event)
        commit(
          'setProcessingState',
          collaborators.adapter.applySessionEvent(state.processingState, event)
        )
      },
      onText: event => {
        if (!current()) return
        received()
        if (settled(event)) return
        delivered(event)
        commit(
          'setProcessingState',
          collaborators.adapter.applySessionEvent(state.processingState, event)
        )
      },
      // AT-API-020-H1: API 0.2.0 has no `ask` event (a turn never pauses), so
      // there is no handler for one.
      //
      // WP-03 binding lifecycle: `done` (never stream EOF) is the
      // completion signal. On `done`, clear the provisional buffer and
      // run the SAME authoritative getSession+getTurns refetch used
      // immediately after createTurn -- the event's own `turn` payload
      // stays unread (PU-008, 08 §8).
      //
      // WP-03-CHAT-CORRECTION-R3 (reference: `setLive(null)` then
      // `loadSession`, which leaves the answer off screen for the whole
      // refetch): the streamed text stays visible in the SAME bubble
      // (`reconciling`) until the authoritative turns have been applied,
      // and only then settles to `done` -- the swap happens inside one
      // task, so there is no vanish/flicker and never two copies.
      onDone: async event => {
        if (!current()) return
        received()
        // Already on screen as a persisted turn (settled from the HTTP
        // response) -- nothing left to reconcile.
        if (settled(event)) return
        delivered(event)
        const live = state.processingState
        if (live && live.text) {
          commit('setProcessingState', {
            phase: 'reconciling',
            turnId: live.turnId,
            text: live.text,
          })
        } else {
          commit(
            'setProcessingState',
            collaborators.adapter.applySessionEvent(
              state.processingState,
              event
            )
          )
        }
        const ok = await dispatch('refetchActiveSession', sessionId)
        // A new reply may already have started; never clobber its state.
        if (ok && state.processingState.phase === 'reconciling') {
          commit('setProcessingState', { phase: 'done' })
        }
      },
      // The Tutor's own `error` event for a reply.
      onError: message => {
        if (!current()) return
        commit(
          'setProcessingState',
          collaborators.adapter.applySessionEvent(state.processingState, {
            type: 'error',
            message,
          })
        )
      },
      // The transport went away (never after `stop()`). Not a reply
      // failure: the chat stays as it is and the stream is reconnected.
      onDisconnect: loss => {
        if (!current()) return
        activeStream = null
        const stable =
          deliveredEvent ||
          Date.now() - connectedAt >= streamReliability.stableMs
        handleStreamLoss(context, sessionId, courseName, loss || {}, stable)
      },
    }
  )
}

// The local object URL behind an optimistic image is released as soon as no
// optimistic message shows it any more.
// BUG-046 / QA-23 (P41) -- the student's message is SENT once it has left the
// browser in full (the createTurn body was transmitted) or the Tutor's event
// stream names a turn of this send, whichever comes first. It no longer waits
// for the reply: the reply's own progress is the live tutor bubble
// (`processingState`), and a send that fails afterwards still ends `failed`.
// `via`: 'transport' | 'stream'. The transport knows its own message
// (`localId`): an earlier request of the same session that finishes
// transmitting late never marks a newer message sent (review P3).
function markPendingSent({ commit, state }, sessionId, via, localId) {
  const pending = state.pendingStudentTurn
  if (!pending || pending.sessionId !== sessionId) return
  if (localId !== undefined && pending.localId !== localId) return
  if (pending.status !== 'sending') return
  commit('setPendingStudentTurn', { ...pending, status: 'sent', sentVia: via })
}

// An SSE event belongs to the message in flight when it names a turn that is
// not one of the session's persisted student turns (`turnId` is the student
// turn being answered). An event of an earlier reply names a landed turn.
function eventOfPendingSend(state, sessionId, event) {
  const turnId = event && event.turnId
  if (!turnId) return false
  return !(state.turnsBySessionId[sessionId] || []).some(
    turn => turn.role === 'student' && turn.id === turnId
  )
}

function releasePreview(previous, next) {
  const url = previous && previous.image && previous.image.previewUrl
  if (!url) return
  if (next && next.image && next.image.previewUrl === url) return
  revokePreviewUrl(url)
}

/**
 * Upserts a turn into a turn list by `turnId` -- replaces in place if a turn
 * with that id already exists, otherwise appends. This single rule is what
 * makes every flow that can legitimately re-deliver the same turn (a
 * `createFeedback` response, a `getTurns` refetch, a future SSE `done.turn`
 * adoption) safe by construction (08 §9).
 * @param {import('@/services/aiTutor/domain').TutorTurn[]} turns
 * @param {import('@/services/aiTutor/domain').TutorTurn} turn
 * @returns {import('@/services/aiTutor/domain').TutorTurn[]}
 */
export function upsertTurn(turns, turn) {
  const index = turns.findIndex(existing => existing.id === turn.id)
  if (index === -1) return [...turns, turn]
  const next = turns.slice()
  next[index] = turn
  return next
}

export default {
  namespaced: true,
  state: {
    /** @type {import('@/services/aiTutor/domain').TutorSession[]} */
    sessions: [],
    /** @type {import('@/services/aiTutor/domain').TutorSession|null} */
    activeSession: null,
    /** @type {import('@/services/aiTutor/domain').TutorSetupContext|null} */
    activeSetupContext: null,
    /** @type {Object.<string, import('@/services/aiTutor/domain').TutorTurn[]>} */
    turnsBySessionId: {},
    /** @type {import('@/services/aiTutor/domain').TutorProcessingState} */
    processingState: { phase: 'idle' },
    /** @type {import('@/services/aiTutor/domain').TutorError|null} */
    error: null,
    // WP-02 -- true only while `startSession`'s `createSession` call is in
    // flight. Distinct from `processingState` (08 §5/§9), which models an
    // active session's turn/SSE lifecycle and does not exist yet at this
    // point in the flow.
    startingSession: false,
    // WP-03-CHAT-CORRECTION-R3 -- 'idle' | 'loading' | 'loaded' | 'error'. A
    // failed session-list load is a soft failure of the list only; it never
    // touches `error`, which belongs to the chat itself.
    sessionsStatus: 'idle',
    // WP-03-CHAT-CORRECTION-R3 -- true from submit until `createTurn` and the
    // immediate refetch have settled (the reference console's `busy`). While
    // it is true the composer is not re-submittable. (STUDENT CHAT V2: the
    // student's message is on screen optimistically meanwhile, so the live
    // tutor reply no longer has to be held back.)
    sendingTurn: false,
    // STUDENT CHAT V2 -- the student's just-sent message, shown immediately
    // (optimistic) until the authoritative refetch returns the persisted
    // student turn. `status`: 'uploading' (its image is being uploaded, no
    // turn requested yet), 'sending' (createTurn in flight), 'sent'
    // (createTurn succeeded, refetch not yet reconciled) or 'failed' (never
    // shown as a persisted turn; the student can resend it).
    // `baselineStudentCount` is how many persisted student turns the session
    // had at send time -- one more than that means this message has landed.
    pendingStudentTurn: null,
    // BUG-22 -- the event stream's transport, independent of any reply:
    // 'idle' (no stream) | 'connecting' | 'open' | 'reconnecting' |
    // 'unavailable' (reconnects exhausted or pointless; the next send tries
    // again).
    streamStatus: 'idle',
    // STUDENT CHAT V2 -- «فهمیدم / نفهمیدم» per tutor turn id, for this page
    // lifetime: {kind, status: 'pending'|'recorded'|'failed', reteachTurnId}.
    // The upstream contract has no read-back for feedback, so a reload shows
    // the prompt again on the latest tutor turn.
    feedbackByTurnId: {},
    // P42-06: how many «فهمیدم / نفهمیدم» taps this page lifetime Subway has
    // CONFIRMED (a 2xx). Reports watches it: a list read while the request was
    // still in transit is then read once more. Never counts a failed tap.
    feedbackRecordedCount: 0,
    // STUDENT CHAT V2 image -- persisted student turn id -> 'camera'|'gallery'
    // for photos sent in this page lifetime (caption only). WB-M2A adds
    // 'board' for a board picture sent in this page lifetime.
    mediaSourceByTurnId: {},
    // WB-M2A (doc 31 §7) -- persisted student turn id -> the Borna share
    // record of the board picture that turn carried ({shareId, scope,
    // snapshotFilename, createdAt}). From Subway's conversation read on
    // open (so the «از تخته» badge survives a reload), and from this page's
    // own sends as they land. Never from the Tutor upstream.
    boardShareByTurnId: {},
    // FINAL CHAT V2 -- the request the V2 setup already described, sent with
    // that session's first turn only. AT-API-020-H1: `{sessionId, actionId}`
    // (adapter `toTutorRequest`) -- the 0.2.0 action the Setup intention
    // names. It is sent only when the course's catalog lists it, and dropped
    // for good when the Tutor refuses it.
    sessionRequest: null,
    // AT-API-020-H1 (doc 34/01 §3.2) -- the Tutor catalog of ONE course,
    // `{courseName, actions: [{id, label, prompt}], teacherHandoff}`, as read
    // through Subway; null until read, and null again when a read fails.
    // `courseName` says whose it is: a catalog is only ever used for the
    // course it was read for.
    catalog: null,
    // 'idle' | 'loading' | 'loaded' | 'error'. A failed read is a soft
    // failure of the catalog only -- it never touches `error`, and turns
    // simply travel as text until a read succeeds.
    catalogStatus: 'idle',
    // AT-API-020-H1 (doc 34/01 §3.3) -- the Tutor refused the ACTION of the
    // message that is on screen as failed: `{sessionId, localId, actionId}`.
    // Nothing was sent again and nothing will be: the Student chooses
    // (`resendWithoutAction` / `dismissActionRecovery`).
    actionRecovery: null,
    // STUDENT-UX-02 / UX2-01 (doc 30 §6, D-1): the Learn setup's OPTIONAL
    // initial question, `{sessionId, text}`, held in memory ONLY between a
    // successful `startSession` and its one consumption by the chat view
    // (which clears it BEFORE dispatching the ordinary `sendMessage`). Never
    // persisted, never part of the session snapshot; every resume path
    // (`openConversation`, `openSession`, `beginNewConversation`, a session
    // change) clears it, and a reload starts without it.
    pendingInitialTurn: null,
  },
  getters: {
    // STUDENT CHAT V2 -- work the student is waiting on with no tutor text on
    // screen yet: a send in flight. AT-API-020-H2: a «نفهمیدم» being recorded
    // is no longer a wait on the Tutor (the press records a Borna learning
    // event and produces no reply), so it is not counted here; the prompt
    // card itself shows the wait with its controls disabled.
    // BUG-046 (P41): a message already SENT whose createTurn is still in flight
    // is still waited on -- the message's label and the reply's wait are two
    // different things.
    awaitingTutorReply: state => {
      const pending = state.pendingStudentTurn
      if (!pending) return false
      if (pending.status === 'sending') return true
      return pending.status === 'sent' && state.sendingTurn
    },
    feedbackInFlight: state =>
      Object.values(state.feedbackByTurnId || {}).some(
        entry => entry.status === 'pending'
      ),
    // AT-API-020-H1 -- the recovery is offered only for the very message it
    // was raised for, while that message is still on screen as failed. A new
    // message, a resend or another conversation ends it by itself.
    actionRecoveryPending: state => {
      const recovery = state.actionRecovery
      const pending = state.pendingStudentTurn
      return Boolean(
        recovery &&
          pending &&
          pending.status === 'failed' &&
          pending.localId === recovery.localId &&
          pending.sessionId === recovery.sessionId
      )
    },
  },
  mutations: {
    setSessions: (state, sessions) => {
      state.sessions = sessions
    },
    setSessionsStatus: (state, status) => {
      state.sessionsStatus = status
    },
    // Replaces a session summary in place (same id) or puts a new one first --
    // the list is newest-first, and a session created or updated in this page
    // lifetime must show up without a list reload.
    upsertSession: (state, session) => {
      const existing = state.sessions || []
      const index = existing.findIndex(item => item.id === session.id)
      if (index === -1) {
        state.sessions = [session, ...existing]
        return
      }
      const next = existing.slice()
      next[index] = session
      state.sessions = next
    },
    setSendingTurn: (state, sendingTurn) => {
      state.sendingTurn = sendingTurn
    },
    setActiveSession: (state, session) => {
      state.activeSession = session
      // UX2-01: the one-shot first turn belongs to exactly one new session.
      const pending = state.pendingInitialTurn
      if (pending && (!session || session.id !== pending.sessionId)) {
        state.pendingInitialTurn = null
      }
    },
    setPendingInitialTurn: (state, pendingInitialTurn) => {
      state.pendingInitialTurn = pendingInitialTurn
    },
    setActiveSetupContext: (state, setupContext) => {
      // Student Journey V2: a new conversation may target a DIFFERENT course
      // than the one whose session list is cached. The list is course-scoped,
      // so drop it (the chat reloads it for the new course); the same course
      // keeps its list and simply gains the new session.
      const previousCourse =
        state.activeSetupContext && state.activeSetupContext.subject
      if (
        previousCourse &&
        setupContext &&
        previousCourse !== setupContext.subject
      ) {
        state.sessions = []
        state.sessionsStatus = 'idle'
      }
      state.activeSetupContext = setupContext
    },
    setStartingSession: (state, startingSession) => {
      state.startingSession = startingSession
    },
    setTurns: (state, { sessionId, turns }) => {
      state.turnsBySessionId = {
        ...state.turnsBySessionId,
        [sessionId]: turns,
      }
    },
    upsertTurn: (state, { sessionId, turn }) => {
      const existing = state.turnsBySessionId[sessionId] || []
      state.turnsBySessionId = {
        ...state.turnsBySessionId,
        [sessionId]: upsertTurn(existing, turn),
      }
    },
    setProcessingState: (state, processingState) => {
      state.processingState = processingState
    },
    setCatalog: (state, catalog) => {
      state.catalog = catalog
    },
    setCatalogStatus: (state, status) => {
      state.catalogStatus = status
    },
    setActionRecovery: (state, recovery) => {
      state.actionRecovery = recovery
    },
    setError: (state, error) => {
      state.error = error
    },
    setPendingStudentTurn: (state, pending) => {
      releasePreview(state.pendingStudentTurn, pending)
      state.pendingStudentTurn = pending
    },
    // Drops the optimistic message once the session's persisted turns include
    // it -- whichever refetch gets there first (createTurn's or SSE `done`'s).
    // STUDENT CHAT V2 image: the persisted turn then shows the upstream's own
    // signed image URL; how the photo was taken (camera/gallery) is not part
    // of the contract, so it is remembered here for this page's caption.
    reconcilePendingStudentTurn: (state, sessionId) => {
      const pending = state.pendingStudentTurn
      if (!pending || pending.sessionId !== sessionId) return
      const turns = state.turnsBySessionId[sessionId]
      if (countStudentTurns(turns) > pending.baselineStudentCount) {
        if (pending.image && pending.image.source) {
          const landed = (turns || []).filter(turn => turn.role === 'student')[
            pending.baselineStudentCount
          ]
          if (landed) {
            state.mediaSourceByTurnId = {
              ...(state.mediaSourceByTurnId || {}),
              [landed.id]: pending.image.source,
            }
            // WB-M2A: the board picture's share record, by the turn it
            // landed as -- the badge needs no server round trip.
            if (pending.boardShare) {
              state.boardShareByTurnId = {
                ...(state.boardShareByTurnId || {}),
                [landed.id]: {
                  shareId: pending.boardShare.shareId,
                  scope: pending.boardShare.scope,
                  snapshotFilename: null,
                  createdAt: pending.createdAt || null,
                },
              }
            }
          }
        }
        releasePreview(pending, null)
        state.pendingStudentTurn = null
      }
    },
    // WB-M2A: the share records Subway holds for the open conversation
    // (adapter `toBoardShareByTurnId`). Merged: a record this page added
    // for a turn Subway has not listed yet is kept.
    setBoardShares: (state, byTurnId) => {
      state.boardShareByTurnId = {
        ...(byTurnId || {}),
        ...(state.boardShareByTurnId || {}),
      }
    },
    setSessionRequest: (state, request) => {
      state.sessionRequest = request
    },
    setStreamStatus: (state, status) => {
      state.streamStatus = status
    },
    setFeedback: (state, { turnId, entry }) => {
      const next = { ...(state.feedbackByTurnId || {}) }
      if (entry) next[turnId] = entry
      else delete next[turnId]
      state.feedbackByTurnId = next
    },
    feedbackRecorded: state => {
      state.feedbackRecordedCount = (state.feedbackRecordedCount || 0) + 1
    },
  },
  actions: {
    // WP-02 -- the setup half of 08 §9's `startSession` ONLY. Preserves the
    // student's setup selection first (frontend-local, before any network
    // call -- 08 §9), then calls the real `createSession`, then commits the
    // resulting `TutorSession`. Does NOT call `sendMessage`/`createTurn` --
    // that tail belongs entirely to WP-03 (09 §6 WP-02 binding correction).
    // A failed `createSession` preserves `activeSetupContext`, never fabricates
    // an `activeSession`, and leaves the student free to retry.
    /**
     * P4 AI Tutor WP-02C-2 (doc 10, doc 11 §B) -- `courseName` is
     * `setupContext.subject`, which already equals `education_courses.name`
     * (ContextStep.vue now requires a real, selected course; doc 11 §H). A
     * missing subject fails locally, before any network call, and never
     * fabricates an `activeSession`.
     * @param {import('@/services/aiTutor/domain').TutorSetupContext} setupContext
     */
    async startSession({ commit, state }, submitted) {
      // UX2-01 (D-1): the optional initial question is NOT setup context --
      // it never reaches `activeSetupContext`, the create DTO / snapshot or the
      // first-turn request. It is held aside and becomes the one-shot first
      // turn only once the NEW session exists.
      const { initialQuestion, ...setupContext } = submitted || {}
      const initialText =
        typeof initialQuestion === 'string' ? initialQuestion.trim() : ''

      // CAT-1A: a retry of a FAILED submission (the setup re-opened on the
      // same selection with `error` set) keeps its request id; anything else
      // is a new submission and gets a fresh one.
      const retrying = Boolean(state && state.error && startRequestId)
      if (!retrying) startRequestId = collaborators.mintRequestId()
      const requestId = startRequestId

      commit('setActiveSetupContext', setupContext)
      // UX2-01: nothing stale may be pending while a new session is created.
      commit('setPendingInitialTurn', null)
      commit('setError', null)
      commit('setStartingSession', true)
      try {
        const courseName = setupContext.subject
        if (!courseName) {
          // Defense in depth: ContextStep.vue's canContinue already blocks
          // submission without a subject (doc 11 §H); this guard protects
          // any other future dispatcher of this action from silently
          // calling createSession('undefined', ...).
          throw {
            kind: 'course_required',
            message: 'A course must be selected',
          }
        }
        const dto = collaborators.adapter.toCreateSessionDto(
          setupContext,
          requestId
        )
        // UX2-01 (UAT retest hardening): a session selection in flight, like
        // `openSession`'s. The student may open another conversation (Reports
        // resume) or leave (`beginNewConversation`) while the create is in
        // flight; the newest selection wins, so a late create never
        // re-activates this session over it. The conversation still exists
        // and is listed in Reports like any other.
        const selection = ++openSeq
        // AT-API-020-H1: the course's catalog is read alongside the create
        // (never awaited here, never able to fail it), so the first turn --
        // which may follow at once (UX2-01) -- finds it read or in flight.
        readCatalog({ commit }, courseName, false)
        const responseDto = await collaborators.api.createSession(
          courseName,
          dto
        )
        if (selection !== openSeq) {
          startRequestId = null
          return
        }
        const session = collaborators.adapter.toTutorSession(responseDto)
        // AT-API-020-H1: the Setup intention as the 0.2.0 action it names.
        const request = collaborators.adapter.toTutorRequest(setupContext)
        commit(
          'setSessionRequest',
          request ? { sessionId: session.id, ...request } : null
        )
        commit('setActiveSession', session)
        commit('upsertSession', session)
        // UX2-01: only a NEW session created here, only a non-empty question.
        commit(
          'setPendingInitialTurn',
          initialText ? { sessionId: session.id, text: initialText } : null
        )
        startRequestId = null
      } catch (err) {
        commit('setError', collaborators.adapter.normalizeTutorError(err))
      } finally {
        commit('setStartingSession', false)
      }
    },

    /**
     * CAT-1A (doc 20 §13, doc 21 §1.1) -- opens a conversation picked from
     * the cross-course Reports catalog. Composes the EXISTING `openSession`
     * flow: the catalog row supplies the course context the chat needs
     * (`activeSetupContext.subject` is the courseName every mediated route
     * is addressed by) plus the recorded setup snapshot for the context bar,
     * then `openSession` does getSession + getTurns exactly as before. Chat
     * session handling is untouched.
     * WB-M2A: `boardShares` (the conversation read's own records) or
     * `conversationId` (the Borna catalog id, read once) gives the chat the
     * share records of the conversation, so a board picture in history keeps
     * its badge. Best-effort: never delays or fails the open.
     * @param {{synapseSessionId: string, courseName: string, intent?: string|null,
     *   grade?: string|null, topic?: string|null, stuckReason?: string|null,
     *   proficiency?: string|null, conversationId?: string|null,
     *   boardShares?: object[]}} conversation
     * @returns {Promise<boolean>} whether that session became active
     */
    async openConversation({ commit, state, dispatch }, conversation) {
      const sessionId = conversation && conversation.synapseSessionId
      const courseName = conversation && conversation.courseName
      if (!sessionId || !courseName) return false
      if (Array.isArray(conversation.boardShares)) {
        commit(
          'setBoardShares',
          collaborators.adapter.toBoardShareByTurnId(conversation.boardShares)
        )
      } else if (conversation.conversationId) {
        // Not a dispatch: the open flow stays exactly openConversation ->
        // openSession for every caller that watches it.
        readBoardShares(commit, conversation.conversationId)
      }
      const previous = state.activeSetupContext || {}
      const sameCourse = previous.subject === courseName
      const context = {
        intent: conversation.intent || (sameCourse ? previous.intent : null),
        grade: conversation.grade || (sameCourse ? previous.grade : null),
        subject: courseName,
        topic: conversation.topic || null,
      }
      if (conversation.stuckReason)
        context.stuckReason = conversation.stuckReason
      if (conversation.proficiency)
        context.proficiency = conversation.proficiency
      commit('setActiveSetupContext', context)
      // UX2-01: a resumed conversation never carries a one-shot first turn.
      commit('setPendingInitialTurn', null)
      await dispatch('openSession', sessionId)
      return Boolean(
        state.activeSession && state.activeSession.id === sessionId
      )
    },

    /**
     * WB-M2A (doc 31 §7) -- reads the conversation's Borna share records
     * (the «از تخته» provenance) from Subway's conversation read. A failure
     * only leaves the badges off; it never touches `error`.
     * @param {string} conversationId -- the Borna catalog id
     */
    loadBoardShares({ commit }, conversationId) {
      return readBoardShares(commit, conversationId)
    },

    /**
     * Student Journey V2 -- "New Conversation". Returns the tutor route to the
     * pre-chat setup by clearing ONLY the open conversation and its transient
     * chat state. Deliberately left alone: `sessions` and
     * `activeSetupContext` (the course-scoped history Reports lists, and the
     * course it is scoped to) and `turnsBySessionId` (cached turns), so no
     * existing session is deleted, replaced or hidden. The next
     * `startSession` creates a NEW session; nothing here calls the API.
     */
    beginNewConversation({ commit }) {
      // UX2-01 (UAT retest hardening): leaving supersedes any session
      // selection still in flight (`openSession`, `startSession`) -- a late
      // response for a conversation the student has left never re-opens it.
      openSeq += 1
      commit('setActiveSession', null)
      // UX2-01: nothing pending survives leaving a conversation.
      commit('setPendingInitialTurn', null)
      commit('setError', null)
      commit('setProcessingState', { phase: 'idle' })
      commit('setSendingTurn', false)
      commit('setPendingStudentTurn', null)
      commit('setActionRecovery', null)
      pendingUpload = null
    },

    /**
     * AT-API-020-H1 (doc 34/01 §3.2) -- reads the Tutor catalog of the
     * current course through Subway. `{force: true}` asks for a fresh read
     * even when one is in flight. Resolves to the catalog, or to null when it
     * could not be read; never rejects and never touches `error`.
     * @param {{force?: boolean}} [options]
     */
    loadCatalog({ commit, state }, options) {
      const courseName = state.activeSetupContext?.subject
      return readCatalog({ commit }, courseName, !!(options && options.force))
    },

    /**
     * WP-03 (doc 09 §WP-03 step 2, doc 10 §13) -- opens the SSE subscription
     * for `activeSession.id`, proxied through Subway. Binding lifecycle rule:
     * this must be dispatched by the chat shell on mount, BEFORE the composer
     * is rendered as usable -- never only after the first `createTurn`.
     * Stops any previously-open subscription first (08 §9's stop-on-
     * session-change rule), so it is always safe to call again when the
     * active session changes.
     */
    openTutorStream({ commit, state, dispatch }) {
      closeTutorStream()
      reconnectAttempt = 0
      const sessionId = state.activeSession?.id
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName) {
        commit('setStreamStatus', 'idle')
        return
      }

      commit('setProcessingState', { phase: 'idle' })
      settledLiveKeys = new Set()
      connectTutorStream({ commit, state, dispatch }, sessionId, courseName)
    },

    /**
     * WP-03 -- stops the current SSE subscription, if any. Called on chat
     * shell unmount and whenever the active session changes (08 §9).
     * BUG-22: also cancels a pending reconnect and any reply recovery.
     */
    stopStreaming({ commit } = {}) {
      closeTutorStream()
      if (commit) commit('setStreamStatus', 'idle')
    },

    /**
     * BUG-22 -- a stream given up on ('unavailable') gets one fresh, bounded
     * attempt when the student acts again (a send or feedback), so live
     * replies come back once the upstream channel has recovered.
     */
    resumeTutorStream({ commit, state, dispatch }) {
      if (state.streamStatus !== 'unavailable') return
      if (activeStream || reconnectTimer) return
      const sessionId = state.activeSession?.id
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName) return
      reconnectAttempt = 0
      connectTutorStream({ commit, state, dispatch }, sessionId, courseName)
    },

    /**
     * BUG-22 -- the stream dropped while a reply was being generated. The
     * reply may well have been persisted anyway (the upstream finishes and
     * stores it regardless of this connection), so the persisted session is
     * re-read on a bounded schedule -- never a resend -- and the live bubble
     * settles as soon as the reply is found. Anything that settles the reply
     * first (a reconnected stream's `done`, the createTurn response) ends
     * this; a reply failure is shown only once the schedule is exhausted.
     * @param {string} sessionId
     */
    async recoverInterruptedReply({ commit, state, dispatch }, sessionId) {
      const turnId = state.processingState.turnId
      const tutorCountAtLoss = countTutorTurns(
        state.turnsBySessionId[sessionId]
      )
      const token = ++recoverySeq
      const stillWaiting = () =>
        token === recoverySeq &&
        state.activeSession?.id === sessionId &&
        ['processing', 'streaming'].includes(state.processingState.phase) &&
        state.processingState.turnId === turnId

      for (const delay of streamReliability.recoveryDelaysMs) {
        await wait(delay)
        if (!stillWaiting()) return
        const ok = await dispatch('refetchActiveSession', {
          sessionId,
          silent: true,
        })
        if (!stillWaiting()) return
        if (
          ok &&
          replyPersisted(
            state.turnsBySessionId[sessionId],
            turnId,
            tutorCountAtLoss
          )
        ) {
          if (turnId) settledLiveKeys.add(turnId)
          commit('setProcessingState', { phase: 'done' })
          return
        }
      }
      if (!stillWaiting()) return
      commit('setProcessingState', {
        phase: 'error',
        error: collaborators.adapter.normalizeTutorError({
          type: 'error',
          message: 'The tutor reply was interrupted',
        }),
      })
    },

    /**
     * WP-03 (doc 09 §WP-03 steps 3-5, doc 08 §9) -- sends one student turn.
     * Calls `createTurn` (response discarded, matching Track B §6 MOCK-API-
     * 005's own documented behavior), then IMMEDIATELY -- not waiting for
     * `done` -- runs the authoritative getSession+getTurns refetch, so the
     * persisted student turn appears right away. SSE (already open via
     * `openTutorStream`) drives the provisional tutor reply independently.
     * @param {{text: string}} input
     */
    //
    // STUDENT CHAT V2 -- CORE INTERACTION LOOP: the upstream `createTurn`
    // only returns once the tutor has FINISHED answering (its reply streams
    // on SSE meanwhile), so the student's message is shown optimistically
    // (`pendingStudentTurn`) from the moment of submit and reconciled away by
    // whichever refetch first returns the persisted student turn. A failed
    // send is re-checked against the server once (it may have landed anyway)
    // and otherwise stays on screen as `failed`, never as a persisted turn.
    //
    // STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW: with an `image` ({file,
    // fileFormat, previewUrl, source} from the composer), the upstream order
    // is upload first -- Subway's `createUploadUrl`, then the bytes straight
    // to the presigned S3 POST -- and only then `createTurn` with
    // `attachments: [{type:'student_image', filename}]`. The optimistic
    // message shows the local preview from the start ('uploading'); the tutor
    // waiting state only begins once `createTurn` is in flight ('sending').
    // An upload that failed is never followed by `createTurn`; a resend after
    // a `createTurn` failure reuses the already-uploaded file.
    //
    // AT-API-020-H1 (API 0.2.0; doc 34/01 §3.1, §3.3) -- what a turn carries:
    // `text`, an optional `actionId` and `attachments` (plus Borna's own
    // `boardShare`). Nothing else: no `mode`, `intake`, `suggestionId` or
    // `resume`, and no second POST of any kind.
    // - `suggestion` ({id, text}): a picked suggested next turn is an
    //   ORDINARY text turn -- its `text` is the message. The local `id` only
    //   keeps the picked card selected while the turn is on its way. A pick
    //   that fails stays on screen as failed and can be resent, like any
    //   message.
    // - the ACTION: a typed message on a session with no student turn yet
    //   asks for the action the Setup intention names (`sessionRequest`); a
    //   caller may also name one explicitly (`request: {actionId}`). Either is
    //   sent ONLY when the catalog read for this course lists it -- the send
    //   waits for that read when it is still in flight -- and otherwise the
    //   message travels as text (an empty catalog, a failed read and an
    //   unlisted id are all the same safe case).
    // - a refused action (`AI_TUTOR_ACTION_REJECTED`: the course's actions
    //   changed after the catalog was read; nothing was recorded or charged):
    //   the catalog is read again, that action is dropped (from the session
    //   and from the message), the message stays on screen as failed with
    //   everything it carried, and `actionRecovery` is raised. It is NEVER
    //   sent again from here: only the Student's own choice resends it
    //   (`resendWithoutAction`, or the message's resend control), and then
    //   without an action.
    // A turn never pauses any more: there is no answer to a pending question
    // (`resume`) to send.
    //
    // WB-M2A (doc 31) -- a BOARD picture: `image.source` is 'board', the
    // file is the frozen snapshot Blob, `boardShare` is the Borna-only share
    // descriptor (stripped by Subway, never sent upstream) and `request` an
    // explicit request of the chosen kind of help, on any turn (not only the
    // first). The same lifecycle as a photo: upload, then createTurn. A
    // resend reuses the frozen picture, the share id and the request, and
    // the upload when it already succeeded.
    // AT-API-020-H4: that request is the chip's 0.2.0 action
    // (`boardShareRequest`: hint -> `solve_exercise`, explain ->
    // `explain_topic`), catalog-gated like any other action; a share may
    // carry no other action (doc 34/01 §3.5), and never inherits the
    // session's Setup action (the H1 P2 rule below).
    async sendMessage(
      { commit, state, dispatch, getters },
      {
        text,
        image,
        suggestion,
        request: explicitRequest,
        boardShare,
        withoutAction,
      }
    ) {
      const sessionId = state.activeSession?.id
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName) return
      // Reference: the composer is disabled while a send is in flight.
      if (state.sendingTurn) return
      if (getters && getters.feedbackInFlight) return
      // A picked suggestion is its text: one without any sends nothing.
      const pickedText =
        suggestion && typeof suggestion.text === 'string'
          ? suggestion.text.trim()
          : ''
      if (suggestion && !pickedText) return
      // A turn needs something to say: a text, or at least a picture. (An
      // answer to a paused question -- `resume` -- no longer exists, so a
      // caller with neither has nothing to send.)
      const hasText = typeof text === 'string' && text.trim().length > 0
      if (!suggestion && !hasText && !(image && image.file)) return
      // A board picture always travels with text (doc 31 §12.2) and as the
      // one image of the turn.
      if (boardShare && (!image || !image.file || !text || !text.trim())) return
      dispatch('resumeTutorStream')

      const messageText = suggestion ? pickedText : text
      const baselineStudentCount = countStudentTurns(
        state.turnsBySessionId[sessionId]
      )
      // The action this message asks for, before the catalog has its say.
      // AT-API-020-H4 (doc 34/01 §3.5): a BOARD share may ask only for a
      // hint or an explanation (`solve_exercise` / `explain_topic`, what its
      // two chips name). Any other explicit action on a share -- above all
      // `check_answer`, which would judge free Board work -- is dropped
      // HERE, before the catalog is consulted, so the share travels as text
      // + picture; Subway refuses the rest as well, but a refused share
      // would stay on screen as failed.
      const explicitActionId =
        !withoutAction &&
        !suggestion &&
        explicitRequest &&
        isActionId(explicitRequest.actionId) &&
        (!boardShare || isBoardShareActionId(explicitRequest.actionId))
          ? explicitRequest.actionId
          : null
      // AT-API-020-H1 P2 correction (independent audit 2026-10-13): a BOARD
      // share never inherits the session's Setup action. Under H1 a share
      // travels with no action unless its caller names one explicitly
      // (H1-13; doc 34/01 §3.5 -- Subway refuses `check_answer` on a share
      // before any lease, and the message would stay refused on every
      // resend). The Setup action remains the session's first ORDINARY
      // turn's, so a share sent first neither carries nor consumes it.
      const sessionActionId =
        !withoutAction &&
        !suggestion &&
        !boardShare &&
        !explicitActionId &&
        baselineStudentCount === 0 &&
        state.sessionRequest &&
        state.sessionRequest.sessionId === sessionId &&
        isActionId(state.sessionRequest.actionId)
          ? state.sessionRequest.actionId
          : null
      const wantedActionId = explicitActionId || sessionActionId
      // AT-API-020-H2: the student's own words after «نفهمیدم» are an ordinary
      // turn (text only -- no action, nothing else travels); its reply is
      // drawn as the re-teach of the turn they clarify. A suggestion pick is
      // the tutor's wording, not the student's clarification.
      const clarifiedTurnId = suggestion
        ? null
        : clarifiedTurnIdFor(
            state.turnsBySessionId[sessionId],
            state.feedbackByTurnId
          )

      const localId = `local-${Date.now()}`
      let upload = null
      if (image && image.file && !suggestion) {
        // A resend of the same image keeps its upload; a new image replaces
        // whatever an earlier failed message was holding.
        upload =
          pendingUpload && pendingUpload.file === image.file
            ? pendingUpload
            : {
                file: image.file,
                fileFormat: image.fileFormat,
                remoteFilename: null,
                boardShare: boardShare || null,
                request: explicitActionId
                  ? { actionId: explicitActionId }
                  : null,
              }
      }
      pendingUpload = upload
      // WB-M2A: the share of a resend is the share it was first sent under.
      const share = upload && upload.boardShare ? upload.boardShare : null

      commit('setError', null)
      commit('setActionRecovery', null)
      commit('setPendingStudentTurn', {
        localId,
        sessionId,
        text: messageText,
        createdAt: new Date().toISOString(),
        status: upload && !upload.remoteFilename ? 'uploading' : 'sending',
        image: upload
          ? { previewUrl: image.previewUrl || '', source: image.source || '' }
          : null,
        boardShare: share
          ? { shareId: share.shareId, scope: share.scope }
          : null,
        request: explicitActionId ? { actionId: explicitActionId } : null,
        suggestion: suggestion
          ? { id: suggestion.id || '', text: pickedText }
          : null,
        baselineStudentCount,
      })
      commit('setSendingTurn', true)
      // A previous reply that already finished (or failed) is no longer
      // "live"; one that is still streaming keeps streaming, as in the
      // reference console.
      const phase = state.processingState.phase
      if (phase !== 'processing' && phase !== 'streaming') {
        commit('setProcessingState', { phase: 'idle' })
      }

      if (upload && !upload.remoteFilename) {
        try {
          const presigned = await collaborators.api.createUploadUrl(
            courseName,
            sessionId,
            { fileFormat: upload.fileFormat }
          )
          await collaborators.api.uploadToPresignedPost(presigned, upload.file)
          upload.remoteFilename = presigned.filename
        } catch (err) {
          // Nothing reached createTurn: no turn exists, so no re-check.
          const pending = state.pendingStudentTurn
          if (pending && pending.localId === localId) {
            commit('setPendingStudentTurn', {
              ...pending,
              status: 'failed',
              failedAt: 'upload',
            })
          }
          commit('setError', collaborators.adapter.normalizeTutorError(err))
          commit('setSendingTurn', false)
          return
        }
        const pending = state.pendingStudentTurn
        if (pending && pending.localId === localId) {
          commit('setPendingStudentTurn', { ...pending, status: 'sending' })
        }
      }

      // AT-API-020-H1: the action travels only when the catalog read for THIS
      // course lists it. The read never rejects; without a catalog the
      // message simply goes as text.
      let actionId = null
      if (wantedActionId) {
        const catalog = await catalogForSend({ commit, state }, courseName)
        actionId = resolveActionId(wantedActionId, catalog)
      }

      try {
        // Exactly ONE createTurn per send -- whatever it answers.
        const reply = await collaborators.api.createTurn(
          courseName,
          sessionId,
          collaborators.adapter.toCreateTurnDto({
            text: messageText,
            actionId,
            attachments: upload
              ? [{ remoteFilename: upload.remoteFilename }]
              : undefined,
            boardShare: share || undefined,
          }),
          // BUG-046: the body has left the browser -- the message is sent.
          {
            onSent: () =>
              markPendingSent(
                { commit, state },
                sessionId,
                'transport',
                localId
              ),
          }
        )
        // The Setup action belongs to the session's first turn only -- sent
        // or not, it is used up once that turn has been accepted.
        if (sessionActionId) commit('setSessionRequest', null)
        syncCreditsFromReply(dispatch, reply)
        await dispatch('refetchActiveSession', sessionId)
        await dispatch('settleLiveReply', reply)
        // AT-API-020-H2: the reply to the clarification is the «نفهمیدم»
        // turn's re-teach -- retry lead on it, retry copy on its prompt, the
        // clarify card closed. Only while that turn still waits for one.
        if (clarifiedTurnId && reply && reply.id) {
          const rated = (state.feedbackByTurnId || {})[clarifiedTurnId]
          if (
            rated &&
            rated.kind === 'confused' &&
            rated.status === 'recorded' &&
            !rated.reteachTurnId
          ) {
            commit('setFeedback', {
              turnId: clarifiedTurnId,
              entry: { ...rated, reteachTurnId: reply.id },
            })
          }
        }
        const pending = state.pendingStudentTurn
        if (pending && pending.sessionId === sessionId) {
          commit('setPendingStudentTurn', { ...pending, status: 'sent' })
        }
      } catch (err) {
        const error = collaborators.adapter.normalizeTutorError(err)
        syncCreditsAfterFailure(dispatch, error)
        // CURRENT CONTRACT AUDIT: the upstream rejected the uploaded file itself
        // (tutor.bad_attachment, e.g. the object is gone) -- a resend must
        // upload again rather than reuse it.
        if (upload && error.code === 'AI_TUTOR_BAD_ATTACHMENT') {
          upload.remoteFilename = null
        }
        // AT-API-020-H1 (doc 34/01 §3.3): the Tutor refused the ACTION this
        // message carried (the course's actions are no longer what the
        // catalog said). Nothing was recorded or charged upstream. That
        // action is dropped for good -- from the session and from the
        // message -- and the catalog is read again (Subway has already
        // dropped its own copy). The message itself is NOT sent again here.
        const actionRejected = !!actionId && error.code === ACTION_REJECTED_CODE
        if (actionRejected) {
          if (
            state.sessionRequest &&
            state.sessionRequest.sessionId === sessionId
          ) {
            commit('setSessionRequest', null)
          }
          if (upload) upload.request = null
          // Not awaited: the Student's message must not wait on it.
          readCatalog({ commit }, courseName, true)
        }
        // UX2-01 (UAT retest hardening): a send that fails after the student
        // has moved to another conversation is that conversation's business
        // only -- its note never appears in the one now open.
        const current = () => state.activeSession?.id === sessionId
        if (current()) commit('setError', error)
        await dispatch('refetchActiveSession', sessionId)
        const pending = state.pendingStudentTurn
        if (pending && pending.sessionId === sessionId) {
          commit('setPendingStudentTurn', {
            ...pending,
            status: 'failed',
            failedAt: actionRejected ? 'action' : 'send',
            request: actionRejected ? null : pending.request,
          })
          if (actionRejected && pending.localId === localId) {
            commit('setActionRecovery', { sessionId, localId, actionId })
          }
        }
        // The original send failure is the one to report, not a secondary
        // refetch failure.
        if (current()) commit('setError', error)
      } finally {
        // UX2-01 (UAT retest hardening): only this message's own send ends the
        // sending state -- never one already in flight for the conversation
        // the student has since opened.
        const pending = state.pendingStudentTurn
        if (!pending || pending.sessionId === sessionId) {
          commit('setSendingTurn', false)
        }
      }
    },

    /**
     * STUDENT CHAT V2 -- resends the optimistic message that failed.
     *
     * AT-API-020-H1: a message whose ACTION the Tutor refused is resent
     * without any action (`withoutAction`) -- whichever control the Student
     * used, and whatever the catalog says by then.
     */
    retryPendingTurn({ state, dispatch }) {
      const pending = state.pendingStudentTurn
      if (!pending || pending.status !== 'failed') return undefined
      const withoutAction = pending.failedAt === 'action'
      if (pending.suggestion) {
        return dispatch('sendMessage', { suggestion: pending.suggestion })
      }
      const image =
        pending.image && pendingUpload
          ? {
              file: pendingUpload.file,
              fileFormat: pendingUpload.fileFormat,
              previewUrl: pending.image.previewUrl,
              source: pending.image.source,
            }
          : null
      // WB-M2A: the same frozen picture, the same share, the same request.
      const held = image && pendingUpload ? pendingUpload : null
      return dispatch('sendMessage', {
        text: pending.text,
        image,
        request: held && held.request ? held.request : undefined,
        boardShare: held && held.boardShare ? held.boardShare : undefined,
        withoutAction: withoutAction || undefined,
      })
    },

    /**
     * AT-API-020-H1 (doc 34/01 §3.3) -- the Student's explicit choice after
     * a refused action: send the SAME message (its text, its picture, its
     * share, an upload that already succeeded) once, as an ordinary question
     * with no action. Does nothing unless that recovery is on screen.
     */
    resendWithoutAction({ commit, getters, dispatch }) {
      if (!getters || !getters.actionRecoveryPending) return undefined
      commit('setActionRecovery', null)
      return dispatch('retryPendingTurn')
    },

    /**
     * AT-API-020-H1 -- the Student's other choice: not now. Nothing is sent;
     * the message stays on screen as failed, with its own resend control.
     * Only the notice (and the error it explained) goes away.
     */
    dismissActionRecovery({ commit, state }) {
      commit('setActionRecovery', null)
      if (state.error && state.error.code === ACTION_REJECTED_CODE) {
        commit('setError', null)
      }
    },

    /**
     * STUDENT CHAT V2 -- `createTurn` and `createFeedback` return the persisted
     * tutor reply only after the same reply has been published on SSE. Once
     * the authoritative turns are applied, a live bubble still drawing that
     * reply (its SSE `turnId` is the reply's `replyToTurnId`) is settled in
     * the same tick, and later SSE events for it are ignored -- the answer is
     * on screen exactly once, whichever channel arrives first.
     * @param {import('@/services/aiTutor/dto').TutorTurnResponseDto} [reply]
     */
    settleLiveReply({ commit, state }, reply) {
      const key = reply && reply.replyToTurnId
      if (!key) return
      settledLiveKeys.add(key)
      const live = state.processingState
      if (live && live.turnId === key) {
        commit('setProcessingState', { phase: 'done' })
      }
    },

    /**
     * STUDENT CHAT V2 -- «فهمیدم» (`understood`) / «نفهمیدم» (`confused`) on a
     * tutor turn, through Subway's mediated feedback route.
     *
     * AT-API-020-H2 (doc 34/01 §3.6, doc 34/07 §6): both are BORNA-OWNED
     * learning events. Subway records the tap durably in its own ledger
     * (feeding the learning Reports) and answers `{id, turnId, kind,
     * recordedAt, repeated}` only once it is written; it never forwards the
     * tap to the Tutor, never produces a reply and never touches a credit.
     * So: `recorded` is set only on a 2xx (never a "recorded" that is not),
     * nothing streams, nothing is upserted, no session refetch. After
     * «نفهمیدم» the student writes what was unclear and sends it as an
     * ordinary turn (`sendMessage`), whose reply then becomes this turn's
     * `reteachTurnId`. One submit per page is in flight at a time; a second
     * tap on a turn already answered `repeated: true` is simply recorded.
     * @param {{turnId: string, kind: 'understood'|'confused'}} input
     */
    async submitFeedback(
      { commit, state, dispatch, getters },
      { turnId, kind }
    ) {
      const sessionId = state.activeSession?.id
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName || !turnId) return
      if (kind !== 'understood' && kind !== 'confused') return
      if (state.sendingTurn || (getters && getters.feedbackInFlight)) return
      dispatch('resumeTutorStream')

      commit('setError', null)
      commit('setFeedback', {
        turnId,
        entry: { kind, status: 'pending', reteachTurnId: null },
      })
      const phase = state.processingState.phase
      if (phase !== 'processing' && phase !== 'streaming') {
        commit('setProcessingState', { phase: 'idle' })
      }
      try {
        await collaborators.api.createFeedback(courseName, sessionId, {
          turnId,
          kind,
        })
        commit('setFeedback', {
          turnId,
          entry: { kind, status: 'recorded', reteachTurnId: null },
        })
        commit('feedbackRecorded')
      } catch (err) {
        commit('setFeedback', {
          turnId,
          entry: { kind, status: 'failed', reteachTurnId: null },
        })
        commit('setError', collaborators.adapter.normalizeTutorError(err))
      }
    },

    /**
     * CURRENT CONTRACT AUDIT -- bounded recovery for an expired signed photo
     * URL: re-reads the session's turns (which upstream re-signs) at most
     * twice per turn, and at most once per coalescing window.
     * @param {string} turnId
     */
    refreshExpiredMedia({ dispatch, state }, turnId) {
      if (!state.activeSession || !turnId) return undefined
      const attempts = mediaRefresh.attempts[turnId] || 0
      if (attempts >= MEDIA_REFRESH_MAX_PER_TURN) return undefined
      const now = Date.now()
      if (now - mediaRefresh.lastAt < MEDIA_REFRESH_COALESCE_MS)
        return undefined
      mediaRefresh.attempts[turnId] = attempts + 1
      mediaRefresh.lastAt = now
      return dispatch('refetchActiveSession', state.activeSession.id)
    },

    /**
     * STUDENT CHAT V2 -- the V2 recovery card's «بازگشت»: back to the
     * understood/not-understood prompt for that turn. The feedback already
     * recorded upstream stays recorded; only this page's view of it resets.
     * @param {string} turnId
     */
    dismissFeedback({ commit }, turnId) {
      commit('setFeedback', { turnId, entry: null })
    },

    /**
     * WP-03 (doc 08 §9) -- the authoritative getSession+getTurns refetch,
     * used both immediately after `createTurn` and again on SSE `done`.
     * Turns arrive newest-first (Track B §6 MOCK-API-004) and are reversed to
     * oldest-first before being applied through the turnId upsert/dedupe
     * rule (`upsertTurn` mutation) -- never a wholesale replace, so a turn
     * that already arrived from another source is updated in place, not
     * duplicated.
     */
    //
    // BUG-22: `{ sessionId, silent: true }` is a background re-read (reply
    // recovery) whose failure is not the student's error to see.
    async refetchActiveSession({ commit, state }, requested) {
      const options =
        requested && typeof requested === 'object'
          ? requested
          : { sessionId: requested }
      const sessionId = options.sessionId || state.activeSession?.id
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName) return false

      const token = ++refetchSeq
      try {
        const [sessionDto, turnsPage] = await Promise.all([
          collaborators.api.getSession(courseName, sessionId),
          collaborators.api.getTurns(courseName, sessionId),
        ])
        // The student may have opened another session while this was in
        // flight: a late response must never overwrite the new active one.
        if (state.activeSession?.id !== sessionId) return false

        if (token === refetchSeq) {
          const session = collaborators.adapter.toTutorSession(sessionDto)
          commit('setActiveSession', session)
          commit('upsertSession', session)
        }
        const turnDtos = Array.isArray(turnsPage?.data) ? turnsPage.data : []
        const oldestFirst = [...turnDtos].reverse()
        oldestFirst.forEach(turnDto => {
          commit('upsertTurn', {
            sessionId,
            turn: collaborators.adapter.toTutorTurn(turnDto),
          })
        })
        commit('reconcilePendingStudentTurn', sessionId)
        if (!state.pendingStudentTurn) pendingUpload = null
        return true
      } catch (err) {
        if (!options.silent && state.activeSession?.id === sessionId) {
          commit('setError', collaborators.adapter.normalizeTutorError(err))
        }
        return false
      }
    },

    /**
     * WP-03-CHAT-CORRECTION-R3 (reference: `refreshSessions`) -- loads the
     * basic session list for the current course. A failure only marks the LIST
     * as failed (`sessionsStatus: 'error'`); it never sets `error`, so the
     * chat itself is unaffected. The active session always stays in the list
     * even if the response does not include it.
     */
    async loadSessions({ commit, state }) {
      const courseName = state.activeSetupContext?.subject
      if (!courseName) return
      commit('setSessionsStatus', 'loading')
      try {
        const page = await collaborators.api.getSessions(courseName)
        const dtos = Array.isArray(page?.data) ? page.data : []
        const list = dtos.map(dto => collaborators.adapter.toTutorSession(dto))
        const active = state.activeSession
        if (active && !list.some(item => item.id === active.id)) {
          list.unshift(active)
        }
        commit('setSessions', list)
        commit('setSessionsStatus', 'loaded')
      } catch (err) {
        // The open conversation stays listed even though the list is missing.
        if (state.activeSession) commit('upsertSession', state.activeSession)
        commit('setSessionsStatus', 'error')
      }
    },

    /**
     * WP-03-CHAT-CORRECTION-R3 (reference: `loadSession(id)` on a list click)
     * -- re-opens an existing session: `getSession` + `getTurns`, then makes
     * it the active session. The previous session's SSE subscription is
     * closed BEFORE the switch (so no stale event can touch the new session);
     * the chat view's `activeSession.id` watcher then opens the new one. On
     * failure the current session stays active and `error` is set. Selecting
     * the session that is already open just refetches it.
     * @param {string} sessionId
     */
    async openSession({ commit, state, dispatch }, sessionId) {
      const courseName = state.activeSetupContext?.subject
      if (!sessionId || !courseName) return
      // UX2-01: opening / re-opening an EXISTING session is never the moment
      // for a one-shot first turn (reopen, Reports resume, Board resume).
      commit('setPendingInitialTurn', null)
      if (state.activeSession?.id === sessionId) {
        await dispatch('refetchActiveSession', sessionId)
        return
      }

      commit('setError', null)
      const token = ++openSeq
      // AT-API-020-H1: the course's catalog is read alongside (never awaited
      // here, never able to fail the open).
      readCatalog({ commit }, courseName, false)
      try {
        const [sessionDto, turnsPage] = await Promise.all([
          collaborators.api.getSession(courseName, sessionId),
          collaborators.api.getTurns(courseName, sessionId),
        ])
        // A newer selection superseded this one while it was in flight.
        if (token !== openSeq) return

        closeTutorStream()
        const session = collaborators.adapter.toTutorSession(sessionDto)
        const turnDtos = Array.isArray(turnsPage?.data) ? turnsPage.data : []
        // Turns first, so the chat never renders the new session empty.
        commit('setTurns', {
          sessionId,
          turns: [...turnDtos]
            .reverse()
            .map(dto => collaborators.adapter.toTutorTurn(dto)),
        })
        commit('setProcessingState', { phase: 'idle' })
        commit('setSendingTurn', false)
        commit('setPendingStudentTurn', null)
        commit('setActionRecovery', null)
        pendingUpload = null
        commit('setActiveSession', session)
        commit('upsertSession', session)
      } catch (err) {
        if (token === openSeq) {
          commit('setError', collaborators.adapter.normalizeTutorError(err))
        }
      }
    },
  },
}
